import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PeerAnalyserTable } from './PeerAnalyserTable'
import { usePeerAnalyserStore } from '../../state-management/peerAnalyserStore'
import { usePeerAnalyserFunds } from '../../hooks/usePeerAnalyserFunds'
import { fetchBrowseCategories } from '../../repository/peer-analyser'
import { buildBrowseCategoriesData, extractCategoriesByFundNature } from '../../utils/buildBrowseCategoriesData'
import { getFundNatureFromCategory } from '../../utils/peerAnalyserUtils'
import { BrowseCategoryItem, FundNature } from '../../types/peer-analyser'
import PeerAnalyserError from '../error-states/PeerAnalyserError'
import { trackEvent, lightImpactVibrate } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { Player } from '@lottiefiles/react-lottie-player'
import { loadingAnimationLottie } from '../ui-components/loading/loadingAnimationData'

function MarqueeIfOverflow({ text, isSelected }: { text: string; isSelected: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [scrollDistance, setScrollDistance] = useState(0)

  const checkOverflow = () => {
    const container = containerRef.current
    const span = textRef.current
    if (!container || !span) return
    const overflowing = span.scrollWidth > container.clientWidth
    setIsOverflowing(overflowing)
    const distance = Math.max(0, span.scrollWidth - container.clientWidth)
    setScrollDistance(distance)
  }

  useEffect(() => {
    checkOverflow()

    // Observe size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    if (textRef.current) resizeObserver.observe(textRef.current)

    // Window resize fallback
    window.addEventListener('resize', checkOverflow)

    // Re-check after fonts load (font metrics affect width)
    if ((document as any).fonts && typeof (document as any).fonts.ready?.then === 'function') {
      ;(document as any).fonts.ready.then(() => checkOverflow())
    }

    // Next-tick re-measure to catch post-render layout
    const raf = requestAnimationFrame(() => checkOverflow())

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', checkOverflow)
      cancelAnimationFrame(raf)
    }
  }, [])

  useEffect(() => {
    // Re-evaluate when selection changes to ensure class toggling is correct
    checkOverflow()
  }, [isSelected])

  return (
    <div ref={containerRef} className='mt-0 w-[68px] max-w-[68px] overflow-hidden text-center'>
      <span
        ref={textRef}
        className={`text-[13px] whitespace-nowrap inline-block ${isSelected && isOverflowing ? 'scroll-text' : ''}`}
        style={{ ['--scroll-distance' as any]: `${scrollDistance}px` }}
      >
        {text}
      </span>
    </div>
  )
}

interface SharedLayoutMenuProps {
  onContentLoaded?: () => void
}

export default function SharedLayoutMenu({ onContentLoaded }: SharedLayoutMenuProps) {
  const selectedCategoryId = usePeerAnalyserStore((state) => state.selectedCategoryId)
  const setSelectedCategoryId = usePeerAnalyserStore((state) => state.setSelectedCategoryId)
  const fundNature = usePeerAnalyserStore((state) => state.fundNature)
  const setFundNature = usePeerAnalyserStore((state) => state.setFundNature)
  const setError = usePeerAnalyserStore((state) => state.setError)
  const clearError = usePeerAnalyserStore((state) => state.clearError)
  const isFtuxEnabled = usePeerAnalyserStore((state) => state.isFtuxEnabled)
  const setIsFtuxEnabled = usePeerAnalyserStore((state) => state.setIsFtuxEnabled)
  const menuRef = useRef<HTMLUListElement>(null)
  const isUserElite = usePeerAnalyserStore((state) => state.isUserElite)
  // Fetch categories from API
  const {
    data: browseCategoriesData,
    isLoading: isLoadingCategories,
    isError: isErrorCategories
  } = useQuery({
    queryKey: ['browseCategories'],
    queryFn: fetchBrowseCategories,
    retry: 1,
    refetchOnWindowFocus: false
  })

  // Build categories from API response and filter by current fund nature - memoized to prevent rebuilding
  const tabs = useMemo(() => {
    return browseCategoriesData ? buildBrowseCategoriesData(browseCategoriesData) : []
  }, [browseCategoriesData])

  const currentFundNature = fundNature || FundNature.EQUITY
  const categories: BrowseCategoryItem[] = useMemo(() => {
    return extractCategoriesByFundNature(tabs, currentFundNature)
  }, [tabs, currentFundNature])

  // Extract category arrays for dot indicators
  const userFundCategories = useMemo(() => {
    return browseCategoriesData?.userFundCategories || []
  }, [browseCategoriesData])

  const paSubscribedCategories = useMemo(() => {
    return browseCategoriesData?.paSubscribedCategories || []
  }, [browseCategoriesData])

  const { error: apiError, refetch, isFetching, isLoading } = usePeerAnalyserFunds()
  const selectedIndex = categories?.findIndex((c) => c?.id === selectedCategoryId) || 0
  const currentSelectionIndex = selectedIndex === -1 ? 0 : selectedIndex

  // FTUX state
  const [showFtux, setShowFtux] = useState(false)

  // Function to scroll clicked item to middle of screen
  const scrollToMiddle = useCallback((clickedElement: HTMLElement) => {
    if (!menuRef.current) return

    const menuContainer = menuRef.current
    const containerWidth = menuContainer.clientWidth
    const elementLeft = clickedElement.offsetLeft
    const elementWidth = clickedElement.offsetWidth

    // Calculate the scroll position to center the element
    const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2

    // Smooth scroll to the calculated position
    menuContainer.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    })
  }, [])

  useEffect(() => {
    // Set initial category and fundNature on mount if not already set
    if (!selectedCategoryId && categories.length > 0) {
      const firstCategory = categories?.[0]
      if (firstCategory?.id) {
        setSelectedCategoryId(firstCategory.id)
        setFundNature(getFundNatureFromCategory(firstCategory.id, browseCategoriesData))
      }
    }
    // If selected category is not in current fund nature's categories, select first category
    else if (selectedCategoryId && categories.length > 0) {
      const categoryExists = categories.some((c) => c?.id === selectedCategoryId)
      if (!categoryExists) {
        const firstCategory = categories?.[0]
        if (firstCategory?.id) {
          setSelectedCategoryId(firstCategory.id)
          setFundNature(getFundNatureFromCategory(firstCategory.id, browseCategoriesData))
        }
      }
    }
  }, [selectedCategoryId, setSelectedCategoryId, setFundNature, categories, currentFundNature])

  // Scroll selected category into view when category or fund nature changes
  // This handles programmatic selection (e.g., from BrowseCategoriesPage)
  useEffect(() => {
    if (!selectedCategoryId || !menuRef.current || categories.length === 0) return

    // Wait for DOM to update, then scroll to the selected category
    const timeoutId = setTimeout(() => {
      if (!menuRef.current) return
      const categoryElements = menuRef.current.children
      if (categoryElements[currentSelectionIndex]) {
        scrollToMiddle(categoryElements[currentSelectionIndex] as HTMLElement)
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [selectedCategoryId, currentSelectionIndex, categories.length, scrollToMiddle])

  // Handle API errors
  useEffect(() => {
    if (apiError && !isFetching) {
      setError(apiError.message || 'Something went wrong')
    } else if (!apiError && !isFetching) {
      clearError()
    }
  }, [apiError, isFetching, setError, clearError])

  // Handle FTUX
  useEffect(() => {
    if (isFtuxEnabled && !isLoading && !isFetching && !apiError) {
      setShowFtux(true)
    } else {
      setShowFtux(false)
    }
  }, [isFtuxEnabled, isLoading, isFetching, apiError, selectedCategoryId])

  // Notify parent when content is loaded
  useEffect(() => {
    if (!isLoading && !isFetching && !apiError && !isLoadingCategories) {
      onContentLoaded?.()
    }
  }, [isLoading, isFetching, apiError, isLoadingCategories, onContentLoaded])

  if (isLoadingCategories) {
    return (
      <div className='w-full bg-white flex flex-col h-full relative'>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <Player autoplay loop src={loadingAnimationLottie} style={{ width: '42px', height: '42px' }} />
            <p className='text-gray-600 text-sm'>Loading categories...</p>
          </div>
        </div>
      </div>
    )
  }

  if (isErrorCategories || !categories || categories?.length === 0) {
    return (
      <div className='w-full bg-white flex flex-col h-full relative'>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <p className='text-gray-600 text-sm'>Error loading categories.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full bg-white flex flex-col h-full relative pt-1'>
      <ul
        ref={menuRef}
        role='tablist'
        aria-label='Fund categories'
        className='flex flex-row flex-shrink-0 gap-3 min-w-16 px-4 overflow-x-auto border-b-[1px] border-grey60 no-scrollbar list-none m-0'
      >
        {categories?.map((cat, idx) => {
          if (!cat?.id) return null

          const IconComponent = cat?.Icon
          const isSelected = currentSelectionIndex === idx
          const iconSelected = isSelected
          const label = cat?.label || ''
          const tabId = `peer-analyser-tab-${cat.id}`
          const enabledCount = categories.filter((c) => c?.id).length
          // Single accessible name from visible label (B04); state-aware hint when not selected (B06)
          const accessibleName = isSelected ? label : `${label}, double tap to select`

          return (
            <li
              key={cat.id}
              role='presentation'
              data-category-id={cat.id}
              className='list-none m-0 p-0 flex-shrink-0'
            >
              <button
                type='button'
                id={tabId}
                role='tab'
                aria-selected={isSelected}
                aria-controls='peer-analyser-category-panel'
                aria-label={accessibleName}
                aria-posinset={idx + 1}
                aria-setsize={enabledCount}
                className={`flex flex-col items-center w-full cursor-pointer focus:outline-none transition-colors border-none bg-transparent p-0
                                ${isSelected ? 'text-[#232323] font-medium' : 'text-[#7c7e80]'}
                            `}
                onClick={(e) => {
                  lightImpactVibrate()

                  // Track category selection
                  trackEvent({
                    eventName: EventNames.ButtonTap,
                    payload: {
                      ScreenName: 'PeerAnalyser',
                      buttonLabel: cat?.label || '',
                      eventId: cat.id
                    }
                  })

                  setSelectedCategoryId(cat.id)
                  setFundNature(getFundNatureFromCategory(cat.id, browseCategoriesData))
                  const scrollTarget = (e.currentTarget.closest('li') as HTMLElement | null) || e.currentTarget
                  scrollToMiddle(scrollTarget)
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
                    return
                  }
                  e.preventDefault()
                  const enabledCategories = categories.filter((c) => c?.id)
                  if (enabledCategories.length === 0) return

                  let nextIndex = idx
                  if (e.key === 'ArrowRight') {
                    nextIndex = (idx + 1) % enabledCategories.length
                  } else if (e.key === 'ArrowLeft') {
                    nextIndex = (idx - 1 + enabledCategories.length) % enabledCategories.length
                  } else if (e.key === 'Home') {
                    nextIndex = 0
                  } else if (e.key === 'End') {
                    nextIndex = enabledCategories.length - 1
                  }

                  const nextCat = enabledCategories[nextIndex]
                  if (!nextCat?.id) return

                  lightImpactVibrate()
                  setSelectedCategoryId(nextCat.id)
                  setFundNature(getFundNatureFromCategory(nextCat.id, browseCategoriesData))

                  const nextEl = menuRef.current?.querySelector(
                    `[id="peer-analyser-tab-${nextCat.id}"]`
                  ) as HTMLElement | null
                  nextEl?.focus()
                  const scrollTarget =
                    (nextEl?.closest('li') as HTMLElement | null) || nextEl
                  if (scrollTarget) scrollToMiddle(scrollTarget)
                }}
              >
                {/* Visual-only children; accessible name comes from aria-label (B04) */}
                <div aria-hidden='true' className='w-[22px] h-[22px] mb-1 flex items-center justify-center relative'>
                  {IconComponent && <IconComponent selected={iconSelected} className='w-full h-full object-contain' />}
                  {/* Dot indicator - orange for paSubscribedCategories (priority), blue for userFundCategories */}
                  {paSubscribedCategories.includes(cat.id) ? (
                    !isUserElite && (
                      <div className='absolute top-0 right-[-8px] w-[6px] h-[6px] rounded-full bg-orange-500' />
                    )
                  ) : userFundCategories.includes(cat.id) ? (
                    <div className='absolute top-0 right-[-8px] w-[6px] h-[6px] rounded-full bg-blue-500' />
                  ) : null}
                </div>
                <div aria-hidden='true'>
                  <MarqueeIfOverflow text={label} isSelected={isSelected} />
                </div>
                {isSelected && (
                  <div aria-hidden='true' className='w-full h-[2px] rounded-full bg-[#232323] mt-3' />
                )}
              </button>
            </li>
          )
        })}
      </ul>
      {apiError ? (
        <div
          role='tabpanel'
          id='peer-analyser-category-panel'
          aria-labelledby={selectedCategoryId ? `peer-analyser-tab-${selectedCategoryId}` : undefined}
          className='flex-1 flex items-center m-8 relative'
        >
          <PeerAnalyserError
            onRetry={() => {
              refetch()
            }}
          />
        </div>
      ) : isLoading || isFetching ? (
        <div
          role='tabpanel'
          id='peer-analyser-category-panel'
          aria-labelledby={selectedCategoryId ? `peer-analyser-tab-${selectedCategoryId}` : undefined}
          className='flex-1 flex items-center justify-center'
        >
          <div className='text-center'>
            <Player autoplay loop src={loadingAnimationLottie} style={{ width: '42px', height: '42px' }} />
            <p className='text-gray-600 text-sm'>Loading funds...</p>
          </div>
        </div>
      ) : (
        <div
          role='tabpanel'
          id='peer-analyser-category-panel'
          aria-labelledby={selectedCategoryId ? `peer-analyser-tab-${selectedCategoryId}` : undefined}
          className='flex-1 min-h-0 flex flex-col'
        >
          <PeerAnalyserTable
            key={selectedCategoryId}
            showFtux={showFtux}
            onFtuxComplete={() => {
              setShowFtux(false)
              setIsFtuxEnabled(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
